import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { clsx } from 'clsx';
import {
  ArrowDown,
  ArrowRight,
  CheckCircle2,
  MessageCircle,
  Pen,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import {
  clearActivityScore,
  readWorkbookJson,
  writeActivityScore,
  writeWorkbookJson,
  type WorkbookActivityCompletion,
} from './workbookPersist';

export type ConvertChoiceExample = { spoken: string; written: string };

export type ConvertChoiceItem = {
  q_no: number;
  spoken: string;
  options: string[];
  correct_answer: string;
};

export interface ConvertWordsChoiceCardProps {
  workbookLabel?: string;
  title: string;
  instructions?: string;
  example: ConvertChoiceExample;
  items: ConvertChoiceItem[];
  sourcePage?: number;
  persistenceId?: string;
  onInteracted?: () => void;
  onWorkbookStatusChange?: (status: WorkbookActivityCompletion) => void;
}

type PersistShape = { selected: Record<number, string> };

function normalizeTamilAnswer(s: string): string {
  let t = String(s).trim().replace(/\s+/g, ' ');
  try {
    t = t.normalize('NFKC');
  } catch {
    /* ignore */
  }
  t = t.replace(/^[\s.,;:!?'"()[\]{}]+|[\s.,;:!?'"()[\]{}]+$/gu, '');
  return t.trim();
}

function rowOutcome(selected: string, correctAnswer: string): 'none' | 'correct' | 'wrong' {
  const s = normalizeTamilAnswer(selected);
  if (!s) return 'none';
  return normalizeTamilAnswer(s) === normalizeTamilAnswer(correctAnswer) ? 'correct' : 'wrong';
}

export const ConvertWordsChoiceCard: React.FC<ConvertWordsChoiceCardProps> = ({
  workbookLabel,
  title,
  instructions,
  example,
  items,
  sourcePage,
  persistenceId,
  onInteracted,
  onWorkbookStatusChange,
}) => {
  const storageKey = persistenceId ? `g3-cwc-${persistenceId}` : '';

  const itemsRef = useRef(items);
  itemsRef.current = items;
  const itemsStableKey = items
    .map((it) => it.q_no)
    .sort((a, b) => a - b)
    .join('|');

  const initialSelected = useMemo(() => {
    const v: Record<number, string> = {};
    for (const it of items) v[it.q_no] = '';
    return v;
  }, [items]);

  const [selected, setSelected] = useState<Record<number, string>>(initialSelected);

  useEffect(() => {
    const list = itemsRef.current;
    const empty: Record<number, string> = {};
    for (const it of list) empty[it.q_no] = '';

    if (!storageKey) {
      setSelected(empty);
      return;
    }
    const loaded = readWorkbookJson<Partial<PersistShape> | null>(storageKey, null);
    const merged = { ...empty };
    if (loaded?.selected && typeof loaded.selected === 'object') {
      for (const k of Object.keys(loaded.selected)) {
        const q = Number(k);
        if (!Number.isFinite(q) || merged[q] === undefined) continue;
        merged[q] = String((loaded.selected as Record<string, unknown>)[k] ?? '');
      }
    }
    setSelected(merged);
  }, [storageKey, itemsStableKey]);

  useEffect(() => {
    if (!storageKey) return;
    writeWorkbookJson(storageKey, { selected } satisfies PersistShape);
  }, [storageKey, selected]);

  const mark = useCallback(() => onInteracted?.(), [onInteracted]);

  const pickOption = (qNo: number, option: string) => {
    mark();
    setSelected((prev) => {
      if (normalizeTamilAnswer(prev[qNo] ?? '').length > 0) return prev;
      return { ...prev, [qNo]: option };
    });
  };

  const resetAll = () => {
    mark();
    setSelected(initialSelected);
    if (storageKey) writeWorkbookJson(storageKey, { selected: initialSelected } satisfies PersistShape);
    if (persistenceId) clearActivityScore(persistenceId);
    onWorkbookStatusChange?.('not_started');
  };

  useEffect(() => {
    if (!onWorkbookStatusChange || items.length === 0) {
      onWorkbookStatusChange?.('not_started');
      return;
    }
    const allFilled = items.every((it) => normalizeTamilAnswer(selected[it.q_no] ?? '').length > 0);
    const anyPick = items.some((it) => normalizeTamilAnswer(selected[it.q_no] ?? '').length > 0);

    if (allFilled && items.length > 0) {
      if (persistenceId) {
        const correct = items.filter(
          (it) => rowOutcome(selected[it.q_no] ?? '', it.correct_answer) === 'correct'
        ).length;
        writeActivityScore(persistenceId, (correct / items.length) * 100);
      }
      onWorkbookStatusChange('completed');
      return;
    }
    if (anyPick) {
      onWorkbookStatusChange('in_progress');
      return;
    }
    onWorkbookStatusChange('not_started');
  }, [onWorkbookStatusChange, items, selected, persistenceId]);

  const allCorrectNow = useMemo(
    () => items.length > 0 && items.every((it) => rowOutcome(selected[it.q_no] ?? '', it.correct_answer) === 'correct'),
    [items, selected]
  );

  const shell = (body: React.ReactNode, footer: React.ReactNode) => (
    <div className="overflow-hidden rounded-[2rem] border-4 border-indigo-200/90 bg-gradient-to-br from-slate-50 via-blue-50/40 to-violet-50/50 shadow-[0_20px_50px_-12px_rgba(79,70,229,0.22)] dark:border-indigo-900/50 dark:from-slate-950 dark:via-indigo-950/30 dark:to-slate-900">
      <div className="border-b border-indigo-100/80 bg-white/70 px-5 py-4 backdrop-blur-sm dark:border-indigo-900/40 dark:bg-slate-900/60">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            {workbookLabel ? (
              <p className="text-sm font-extrabold text-indigo-700 tamil-text dark:text-indigo-300" lang="ta">
                {workbookLabel}
              </p>
            ) : null}
            <h2 className="mt-1 text-2xl font-extrabold leading-snug text-slate-900 tamil-text md:text-3xl dark:text-slate-50" lang="ta">
              {title}
            </h2>
            {instructions ? (
              <p className="mt-3 text-lg font-semibold leading-relaxed text-slate-700 tamil-text dark:text-slate-200/90" lang="ta">
                {instructions}
              </p>
            ) : null}
          </div>
          {sourcePage != null ? (
            <span className="rounded-full bg-indigo-600 px-3 py-1 text-sm font-bold text-white shadow-md">p.{sourcePage}</span>
          ) : null}
        </div>
      </div>
      <div className="px-4 py-6 sm:px-6">{body}</div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-t border-indigo-100/70 bg-white/50 px-4 py-4 backdrop-blur-sm dark:border-indigo-900/35 dark:bg-slate-900/50 sm:px-6">
        {footer}
      </div>
    </div>
  );

  const footer = (
    <div className="flex w-full flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
      <button
        type="button"
        onClick={resetAll}
        className="inline-flex min-h-[52px] items-center gap-2 rounded-2xl border-2 border-slate-300 bg-white px-5 py-3 text-base font-bold text-slate-800 shadow-sm transition hover:border-indigo-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700 tamil-text"
        lang="ta"
      >
        <RotateCcw className="h-5 w-5 shrink-0" aria-hidden />
        மீண்டும் தொடங்கு
      </button>
      {allCorrectNow ? (
        <p className="flex items-center justify-center gap-2 text-center text-lg font-black text-emerald-700 tamil-text dark:text-emerald-300 sm:justify-end" lang="ta">
          <CheckCircle2 className="h-7 w-7 shrink-0 text-emerald-600" aria-hidden />
          அனைத்தும் சரி!
        </p>
      ) : (
        <p className="text-center text-sm font-bold text-slate-700 tamil-text dark:text-slate-200 sm:text-right" lang="ta">
          மூன்று விடைகளில் ஒன்றைத் தட்டி எழுத்துவழக்கைத் தெரிவு செய்யவும்.
        </p>
      )}
    </div>
  );

  return shell(
    <div className="space-y-6">
      <section
        className="rounded-2xl border-2 border-indigo-200/80 bg-white/80 p-5 shadow-md dark:border-indigo-900/50 dark:bg-slate-900/40"
        aria-label="எடுத்துக்காட்டு"
      >
        <div className="mb-4 flex flex-wrap items-center gap-2">
          <Sparkles className="h-6 w-6 shrink-0 text-indigo-500 dark:text-indigo-400" aria-hidden />
          <h3 className="text-lg font-black text-indigo-950 tamil-text dark:text-indigo-100" lang="ta">
            எ.கா
          </h3>
          <span
            className="ml-auto hidden items-center gap-1.5 rounded-full bg-slate-100 px-3 py-1 text-xs font-black uppercase tracking-wide text-slate-600 dark:bg-slate-800 dark:text-slate-300 sm:flex"
            aria-hidden
          >
            <span className="text-blue-600 dark:text-blue-400">கேள்வி</span>
            <ArrowRight className="h-3.5 w-3.5 text-violet-500" />
            <span className="text-violet-700 dark:text-violet-300">விடை</span>
          </span>
        </div>
        <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-stretch sm:gap-2">
          <div className="flex min-h-[88px] flex-1 flex-col justify-center rounded-2xl border-2 border-blue-400/90 bg-gradient-to-br from-blue-50 to-sky-50/80 p-4 shadow-sm dark:border-blue-500 dark:from-blue-950/50 dark:to-slate-900/80">
            <p className="flex items-center gap-2 text-sm font-black text-blue-900 tamil-text dark:text-blue-200" lang="ta">
              <MessageCircle className="h-5 w-5 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
              பேச்சுவழக்கு
            </p>
            <p className="mt-2 text-2xl font-black leading-snug text-blue-950 tamil-text dark:text-blue-50" lang="ta">
              {example.spoken}
            </p>
          </div>
          <div
            className="flex shrink-0 items-center justify-center py-1 sm:flex-col sm:justify-center sm:px-1"
            aria-hidden
          >
            <ArrowDown className="h-7 w-7 text-violet-400 sm:hidden" strokeWidth={2.5} />
            <div className="hidden flex-col items-center sm:flex" title="பேச்சு → எழுத்து">
              <ArrowRight className="h-8 w-8 text-violet-500 dark:text-violet-400" strokeWidth={2.5} />
            </div>
          </div>
          <div className="flex min-h-[88px] flex-1 flex-col justify-center rounded-2xl border-2 border-dashed border-violet-400 bg-gradient-to-br from-violet-50 to-purple-50/70 p-4 shadow-sm dark:border-violet-500 dark:from-violet-950/45 dark:to-slate-900/80">
            <p className="flex items-center gap-2 text-sm font-black text-violet-900 tamil-text dark:text-violet-200" lang="ta">
              <Pen className="h-5 w-5 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
              எழுத்துவழக்கு
            </p>
            <p className="mt-2 text-2xl font-black leading-snug text-violet-950 tamil-text dark:text-violet-50" lang="ta">
              {example.written}
            </p>
          </div>
        </div>
      </section>

      <div className="space-y-5">
        <h3 className="text-center text-base font-black text-slate-800 tamil-text dark:text-slate-100" lang="ta">
          உன் பயிற்சி
        </h3>
        {items.map((it) => {
          const sel = selected[it.q_no] ?? '';
          const outcome = rowOutcome(sel, it.correct_answer);
          return (
            <div
              key={it.q_no}
              className={clsx(
                'rounded-2xl border-2 p-4 shadow-md transition sm:p-5',
                outcome === 'correct'
                  ? 'border-emerald-300 bg-emerald-50/40 dark:border-emerald-600/60 dark:bg-emerald-950/20'
                  : outcome === 'wrong'
                    ? 'border-rose-300 bg-rose-50/50 dark:border-rose-600/50 dark:bg-rose-950/25'
                    : 'border-slate-200/90 bg-white/95 dark:border-slate-600 dark:bg-slate-900/70'
              )}
              role="group"
              aria-label={`வினா ${it.q_no}`}
            >
              <div className="flex flex-col gap-2 sm:flex-row sm:items-stretch sm:gap-2">
                <div className="flex min-h-[76px] flex-1 flex-col justify-center rounded-2xl border-2 border-blue-400 bg-gradient-to-br from-blue-50 to-sky-50/90 px-4 py-3 shadow-sm dark:border-blue-500 dark:from-blue-950/55 dark:to-slate-900/90">
                  <p className="flex items-center gap-2 text-xs font-black uppercase tracking-wide text-blue-900 tamil-text dark:text-blue-200" lang="ta">
                    <MessageCircle className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" aria-hidden />
                    பேச்சுவழக்கு
                  </p>
                  <p className="mt-1.5 text-xl font-black leading-snug text-blue-950 tamil-text sm:text-2xl dark:text-blue-50" lang="ta">
                    {it.spoken}
                  </p>
                </div>

                <div
                  className="flex shrink-0 flex-row items-center justify-center gap-1 py-1 sm:flex-col sm:justify-center sm:px-0.5"
                  aria-hidden
                >
                  <ArrowDown className="h-6 w-6 text-violet-400 sm:hidden" strokeWidth={2.5} />
                  <div className="hidden flex-col items-center sm:flex" title="பேச்சு → எழுத்து">
                    <ArrowRight className="h-7 w-7 text-violet-500 dark:text-violet-400" strokeWidth={2.5} />
                  </div>
                </div>

                <div className="flex min-w-0 flex-1 flex-col gap-2">
                  <p className="flex items-center gap-2 text-xs font-black uppercase tracking-wide text-violet-900 tamil-text dark:text-violet-200" lang="ta">
                    <Pen className="h-4 w-4 shrink-0 text-violet-600 dark:text-violet-400" aria-hidden />
                    எழுத்துவழக்கு
                  </p>
                  <div
                    className={clsx(
                      'flex min-h-[56px] items-center justify-center rounded-xl border-2 border-dashed px-3 py-2 text-center text-xl font-black shadow-inner sm:text-2xl',
                      outcome === 'correct'
                        ? 'border-emerald-500 bg-emerald-50/90 text-emerald-950 dark:border-emerald-400 dark:bg-emerald-950/55 dark:text-emerald-50'
                        : outcome === 'wrong'
                          ? 'border-rose-500 bg-rose-50/90 text-rose-950 dark:border-rose-400 dark:bg-rose-950/45 dark:text-rose-50'
                          : 'border-violet-400 bg-violet-50/80 text-violet-950 dark:border-violet-500 dark:bg-violet-950/40 dark:text-violet-50'
                    )}
                    lang="ta"
                    aria-live="polite"
                  >
                    {sel ? (
                      <span className="tamil-text">{sel}</span>
                    ) : (
                      <span className="text-base font-bold tracking-[0.15em] text-violet-400/70 dark:text-violet-300/45">
                        தேர்வு செய்
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2 sm:gap-3" role="list">
                {it.options.map((opt, oi) => {
                  const isChosen = normalizeTamilAnswer(sel) === normalizeTamilAnswer(opt);
                  const rowLocked = normalizeTamilAnswer(sel).length > 0;
                  return (
                    <button
                      key={`${it.q_no}-${oi}`}
                      type="button"
                      role="listitem"
                      disabled={rowLocked && !isChosen}
                      onClick={() => pickOption(it.q_no, opt)}
                      className={clsx(
                        'min-h-[48px] flex-1 rounded-full border-2 px-4 py-2.5 text-lg font-black shadow-sm transition active:scale-[0.98] tamil-text sm:min-w-[120px] sm:flex-none sm:text-xl',
                        isChosen && outcome === 'correct'
                          ? 'border-emerald-500 bg-emerald-100 text-emerald-950 ring-2 ring-emerald-400/80 dark:border-emerald-400 dark:bg-emerald-900/55 dark:text-emerald-50'
                          : isChosen && outcome === 'wrong'
                            ? 'border-rose-500 bg-rose-100 text-rose-950 ring-2 ring-rose-400/80 dark:border-rose-400 dark:bg-rose-900/50 dark:text-rose-50'
                            : 'border-slate-200 bg-white text-slate-900 hover:border-slate-300 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:border-slate-500 dark:hover:bg-slate-700'
                      )}
                      aria-pressed={isChosen}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>

              {outcome === 'correct' ? (
                <p className="mt-3 text-sm font-black text-emerald-700 tamil-text dark:text-emerald-300" lang="ta">
                  சரி!
                </p>
              ) : null}
            </div>
          );
        })}
      </div>
    </div>,
    footer
  );
};
